import { useOktaAuth } from "@okta/okta-react";
import { useEffect, type ReactNode } from "react";

// Mirrors the API's OKTA_ADMIN_GROUP (default "jump-pipelines-admins"). The API
// enforces membership server-side (403 auth.forbidden); we check the same claim
// client-side so a signed-in non-member gets a plain explanation instead of
// empty screens (every /api call would otherwise 403). Override per-env if the
// API's group is overridden. (DEV-5058)
const ADMIN_GROUP = import.meta.env.VITE_OKTA_ADMIN_GROUP || "jump-pipelines-admins";

function NotAuthorized({ email, group }: { email?: string; group: string }) {
  return (
    <div style={{ padding: 24, maxWidth: 520, fontFamily: "var(--font-body)", color: "var(--color-text)" }}>
      <div className="uppercase-label" style={{ marginBottom: 8 }}>Not authorized</div>
      <p style={{ color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
        You're signed in{email ? ` as ${email}` : ""}, but your account isn't authorized to use Anansi yet.
        Ask an admin to add you to the <code>{group}</code> group, then sign in again.
      </p>
    </div>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { authState, oktaAuth } = useOktaAuth();

  useEffect(() => {
    if (authState && !authState.isAuthenticated) {
      oktaAuth.signInWithRedirect();
    }
  }, [authState, oktaAuth]);

  if (!authState || !authState.isAuthenticated) {
    return (
      <div style={{ padding: 24, fontFamily: "var(--font-mono)", color: "var(--color-text-secondary)" }}>
        Redirecting…
      </div>
    );
  }

  // Authenticated — now check authorization against the same group the API gates on.
  const claims = authState.accessToken?.claims as { groups?: unknown } | undefined;
  const groups = Array.isArray(claims?.groups) ? (claims!.groups as unknown[]).map(String) : undefined;
  // Only lock out when we can POSITIVELY confirm the group is absent. If the token
  // carries no groups claim at all, fall through and let the server-side 403 remain
  // the backstop — never false-lock a real admin over a token-shape quirk.
  if (groups && !groups.includes(ADMIN_GROUP)) {
    const email = (authState.idToken?.claims as { email?: string } | undefined)?.email;
    return <NotAuthorized email={email} group={ADMIN_GROUP} />;
  }
  return <>{children}</>;
}
